import {
  useMemo,
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import Translation from "../Subtitles/Translation";
import { UserVideoWord } from "@shared_types";
import { PlacementTestHighlightedWordsRef } from "./PlacementTestHighlightedWords";
import PlacementTestSubtitle from "./PlacementTestSubtitle";
import {
  calculatePivotsByPixelWidth,
  calcWordDisplayIndex,
} from "../Video/VideoContent";
import { usePhrase } from "../Video/usePhrase";

interface PlacementTestContentProps {
  currentTime: number;
  userVideoWords: UserVideoWord[];
  onWordClick: (clickedWord: UserVideoWord) => void;
  onPhraseSelectStart?: () => void;
  isDisplayHelp: boolean;
  targetWord: string;
  nativeWord: string;
  isHorizontal: boolean;
}

// Add an export interface for the PlacementTestContent ref
export interface PlacementTestContentRef {
  clearPhraseSelection: () => void;
}

const PlacementTestContent = forwardRef<
  PlacementTestContentRef,
  PlacementTestContentProps
>(
  (
    {
      currentTime,
      userVideoWords,
      onWordClick,
      onPhraseSelectStart,
      isDisplayHelp,
      targetWord,
      nativeWord,
    },
    ref
  ) => {
    // Add a ref for the PlacementTestHighlightedWords component
    const highlightedWordsRef = useRef<PlacementTestHighlightedWordsRef>(null);

    // Add phrase selection state
    const [hasPhraseSelection, setHasPhraseSelection] = useState(false);
    const [phraseStartWordId, setPhraseStartWordId] = useState<
      number | undefined
    >(undefined);
    const [phraseEndWordId, setPhraseEndWordId] = useState<number | undefined>(
      undefined
    );

    let firstWordIdx = 0;
    let lastWordIdx = 0;
    let leftMostWordIdx = 0;
    let rightMostWordIdx = 0;
    let selectedPhraseString = "";
    if (phraseStartWordId !== undefined && phraseEndWordId !== undefined) {
      firstWordIdx = userVideoWords.findIndex(
        (word) => word.videoWordId === phraseStartWordId
      );
      lastWordIdx = userVideoWords.findIndex(
        (word) => word.videoWordId === phraseEndWordId
      );
      leftMostWordIdx = Math.min(firstWordIdx, lastWordIdx);
      rightMostWordIdx = Math.max(firstWordIdx, lastWordIdx);
      selectedPhraseString = userVideoWords
        .slice(leftMostWordIdx, rightMostWordIdx + 1)
        .map((word) => word.displayText)
        .join(" ");
    }

    // Add usePhrase hook
    const {
      phraseTranslation,
      isLoading: isPhraseLoading,
      isError,
    } = usePhrase(phraseStartWordId, phraseEndWordId, selectedPhraseString);

    // Clear the phrase selection
    const clearPhraseSelection = () => {
      setHasPhraseSelection(false);
      setPhraseStartWordId(undefined);
      setPhraseEndWordId(undefined);
    };

    // Expose the clearPhraseSelection method to parent components
    useImperativeHandle(ref, () => ({
      clearPhraseSelection: () => {
        // Call the PlacementTestHighlightedWords clearPhraseSelection method
        highlightedWordsRef.current?.clearPhraseSelection();
        // Also clear the local phrase selection state
        clearPhraseSelection();
      },
    }));

    const handlePhraseSelectStart = () => {
      setPhraseStartWordId(undefined);
      setPhraseEndWordId(undefined);
      if (onPhraseSelectStart) {
        onPhraseSelectStart();
      }
    };

    const handlePhraseSelectDone = (
      startWord: UserVideoWord,
      endWord: UserVideoWord
    ) => {
      // dont allow length of 1
      if (startWord.videoWordId === endWord.videoWordId) {
        return;
      }
      // Save the word IDs for highlighting
      setPhraseStartWordId(startWord.videoWordId);
      setPhraseEndWordId(endWord.videoWordId);
      // Mark that we have a selection
      setHasPhraseSelection(true);
    };

    const paragraphRef = highlightedWordsRef.current?.getParagraphRef();
    const [paragraphSize, setParagraphSize] = useState<DOMRect | null>(null);
    const [fontSize, setFontSize] = useState<string>("16px");
    const [fontFamily, setFontFamily] = useState<string>("Inter, sans-sarif");

    useEffect(() => {
      if (!paragraphRef) return;

      const updateSize = () => {
        const size = paragraphRef.getBoundingClientRect();
        setParagraphSize(size);
        const computedStyle = getComputedStyle(paragraphRef);
        const fontSize = computedStyle.fontSize;
        const fontFamily = computedStyle.fontFamily;
        setFontSize(fontSize);
        setFontFamily(fontFamily);
      };

      updateSize(); // Initial measurement

      const observer = new ResizeObserver(() => {
        updateSize(); // Re-measure on resize
      });

      observer.observe(paragraphRef);

      return () => observer.disconnect();
    }, [paragraphRef]);

    const fontForPivots = `${fontSize} ${fontFamily}`;
    const defaultPsizePx = 192;

    const pivots = useMemo(
      () =>
        calculatePivotsByPixelWidth(
          userVideoWords,
          paragraphSize?.width || defaultPsizePx,
          fontForPivots
        ),
      [userVideoWords, paragraphSize, fontForPivots]
    );

    const highlightIndex = calcWordDisplayIndex(currentTime, userVideoWords);
    const selectedPivot =
      pivots.find(
        (pivot) => pivot[0] <= highlightIndex && highlightIndex <= pivot[1]
      ) || pivots[pivots.length - 1];

    // fix out of bounds if we screwed up
    if (selectedPivot[1] >= userVideoWords.length) {
      console.log(
        "WARNING: out of bounds selectedPivot[1] >= userVideoWords.length"
      );
      selectedPivot[1] = userVideoWords.length - 1;
    }

    const toShowUserWords = userVideoWords.slice(
      selectedPivot[0],
      selectedPivot[1] + 1
    );

    const roundedNessCls = "rounded-xl";

    // this is the index relative to the words that are being shown
    const showWordsHighlightIndex = highlightIndex - selectedPivot[0];

    const handleWordClick = (word: UserVideoWord) => {
      onWordClick(word);
      if (hasPhraseSelection) {
        clearPhraseSelection();
      }
    };

    return (
      <div
        className={`h-full relative flex flex-col justify-start w-full ${roundedNessCls} p-4 flex-grow bg-[#fafafa]`}
      >
        <PlacementTestSubtitle
          ref={highlightedWordsRef}
          highlightIndex={showWordsHighlightIndex}
          userVideoWords={toShowUserWords}
          onWordClick={handleWordClick}
          onPhraseSelectedDone={handlePhraseSelectDone}
          onPhraseSelectStart={handlePhraseSelectStart}
        />
        {!isDisplayHelp && (
          <div className="pt-4">
            {hasPhraseSelection ? (
              <Translation
                phraseTranslation={phraseTranslation}
                targetWord={targetWord}
                nativeWord={nativeWord}
                isPhraseLoading={isPhraseLoading}
                isPhraseError={isError}
              />
            ) : (
              <Translation targetWord={targetWord} nativeWord={nativeWord} />
            )}
          </div>
        )}
      </div>
    );
  }
);

export default PlacementTestContent;
